import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CalendarCheck, Check, Loader2 } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useSite, wa } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("ada_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("ada_chat_session", id);
  }
  return id;
};

const sanitize = (text) =>
  text
    .replace(/\[WHATSAPP\]/g, "")
    .replace(/\[RANDEVU\]/g, "")
    .replace(/\[[^\]]*\]/g, "")
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/`/g, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const GREETING = {
  role: "assistant",
  text: "Merhaba, ben Ada Asistan. Samsun Ada Kız Öğrenci Yurdu hakkında merak ettiklerinizi yanıtlamaktan memnuniyet duyarım. Size nasıl yardımcı olabilirim?",
};

const SUGGESTIONS = ["Odalarınız hakkında bilgi alabilir miyim?", "Hangi hizmetler sunuyorsunuz?", "Konumunuz nerede?", "Fiyat bilgisi almak istiyorum"];

const TIME_SLOTS = ["10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const buildDays = () => {
  const out = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push({
      iso,
      weekday: i === 0 ? "Bugün" : i === 1 ? "Yarın" : d.toLocaleDateString("tr-TR", { weekday: "short" }),
      day: d.getDate(),
      month: d.toLocaleDateString("tr-TR", { month: "short" }),
    });
  }
  return out;
};

const prettyDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" });
};

const spring = { type: "spring", stiffness: 500, damping: 32 };

function AppointmentForm() {
  const days = useRef(buildDays()).current;
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (name.trim().length < 2 || surname.trim().length < 2 || phone.trim().length < 7) {
      setErr("Lütfen ad, soyad ve telefon bilgilerinizi eksiksiz girin.");
      return;
    }
    if (!date || !time) {
      setErr("Lütfen tercih ettiğiniz gün ve saati seçin.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/appointment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: getSessionId(),
          name: name.trim(),
          surname: surname.trim(),
          phone: phone.trim(),
          preferred_date: date,
          preferred_time: time,
        }),
      });
      if (!res.ok) throw new Error("fail");
      setDone(true);
    } catch {
      setErr("Randevu oluşturulamadı. Lütfen tekrar deneyin veya WhatsApp hattımızdan ulaşın.");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="appointment-success"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="mt-2 flex items-start gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-relaxed text-emerald-800"
      >
        <Check className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Teşekkürler {name.trim()}. <strong className="font-semibold">{prettyDate(date)} · {time}</strong> için randevu talebiniz alınmıştır; ekibimiz en kısa sürede sizinle iletişime geçecektir.
        </span>
      </motion.div>
    );
  }

  return (
    <motion.form
      data-testid="appointment-form"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      onSubmit={submit}
      className="mt-2 space-y-3 rounded-2xl border border-charcoal-900/10 bg-white p-3.5 shadow-sm"
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold text-charcoal-900">
        <CalendarCheck className="h-3.5 w-3.5 text-gold-dark" />
        Randevu Oluştur
      </p>

      <div className="grid grid-cols-2 gap-2">
        <input
          data-testid="appointment-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Adınız"
          maxLength={60}
          className="rounded-xl border border-charcoal-900/10 bg-cream-50 px-3 py-2 text-xs text-charcoal-900 outline-none transition-colors placeholder:text-charcoal-900/40 focus:border-charcoal-900/30"
        />
        <input
          data-testid="appointment-surname-input"
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          placeholder="Soyadınız"
          maxLength={60}
          className="rounded-xl border border-charcoal-900/10 bg-cream-50 px-3 py-2 text-xs text-charcoal-900 outline-none transition-colors placeholder:text-charcoal-900/40 focus:border-charcoal-900/30"
        />
      </div>
      <input
        data-testid="appointment-phone-input"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Telefon numaranız"
        inputMode="tel"
        maxLength={25}
        className="w-full rounded-xl border border-charcoal-900/10 bg-cream-50 px-3 py-2 text-xs text-charcoal-900 outline-none transition-colors placeholder:text-charcoal-900/40 focus:border-charcoal-900/30"
      />

      <div>
        <p className="mb-1.5 font-accent text-[10px] uppercase tracking-[0.18em] text-charcoal-900/45">
          Tercih Edilen Gün
        </p>
        <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {days.map((d) => {
            const active = date === d.iso;
            return (
              <motion.button
                key={d.iso}
                type="button"
                data-testid={`appointment-day-${d.iso}`}
                onClick={() => setDate(d.iso)}
                whileTap={{ scale: 0.94 }}
                transition={spring}
                className={`relative flex min-w-[3.1rem] shrink-0 flex-col items-center gap-0.5 rounded-xl border px-2 py-2 transition-colors duration-300 ${
                  active
                    ? "border-transparent text-cream-50"
                    : "border-charcoal-900/10 bg-cream-50 text-charcoal-900/70 hover:border-charcoal-900/25"
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="appt-day-pill"
                    transition={spring}
                    className="absolute inset-0 rounded-xl bg-charcoal-900"
                  />
                )}
                <span className="relative text-[9px] font-medium uppercase tracking-wide">{d.weekday}</span>
                <span className="relative text-sm font-semibold leading-none">{d.day}</span>
                <span className="relative text-[9px] opacity-70">{d.month}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {date && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="mb-1.5 font-accent text-[10px] uppercase tracking-[0.18em] text-charcoal-900/45">
              Tercih Edilen Saat
            </p>
            <div className="flex flex-wrap gap-1.5">
              {TIME_SLOTS.map((t) => {
                const active = time === t;
                return (
                  <motion.button
                    key={t}
                    type="button"
                    data-testid={`appointment-time-${t}`}
                    onClick={() => setTime(t)}
                    whileTap={{ scale: 0.94 }}
                    transition={spring}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors duration-300 ${
                      active
                        ? "border-transparent bg-gold-dark text-white"
                        : "border-charcoal-900/10 bg-cream-50 text-charcoal-900/70 hover:border-charcoal-900/25"
                    }`}
                  >
                    {t}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {err && <p className="text-[11px] font-medium text-rose-600">{err}</p>}
      <button
        data-testid="appointment-submit-button"
        type="submit"
        disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-charcoal-900 py-2.5 text-xs font-semibold text-cream-50 transition-all duration-300 ease-premium hover:bg-gold-dark disabled:opacity-60"
      >
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {busy ? "Gönderiliyor..." : "Randevu Talebi Gönder"}
      </button>
    </motion.form>
  );
}

export default function ChatWidget() {
  const D = useSite();
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ada_chat_lead") || "null"); } catch { return null; }
  });
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "", pending: true }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("network");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            full += data.delta;
            const shown = sanitize(full.replace(/\[\w*$/, ""));
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: shown, pending: true };
              return copy;
            });
          }
          if (data.error) full += `\n${data.error}`;
        }
      }
      const whatsapp = full.includes("[WHATSAPP]");
      const appointment = full.includes("[RANDEVU]");
      const finalText = sanitize(full).trim() ||
        "Asistan şu anda yanıt veremiyor. Lütfen WhatsApp hattımızdan bize ulaşın.";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: finalText, whatsapp: (whatsapp || !full.trim()) && !appointment, appointment };
        return copy;
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Bağlantı sırasında bir sorun oluştu. Dilerseniz WhatsApp hattımızdan bize anında ulaşabilirsiniz.",
          whatsapp: true,
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ada Asistan ile sohbet edin"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.7, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="group fixed bottom-24 right-6 z-50 flex items-center md:bottom-28 md:right-8"
      >
        <span className="pointer-events-none absolute right-full mr-4 hidden whitespace-nowrap rounded-full border border-charcoal-900/5 bg-white/95 px-5 py-2.5 text-sm font-medium text-charcoal-900 opacity-0 shadow-lift backdrop-blur transition-all duration-500 ease-premium group-hover:-translate-x-1 group-hover:opacity-100 md:block">
          Ada Asistan'a Sorun
        </span>
        <span className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-charcoal-900 shadow-lift ring-1 ring-white/10 transition-transform duration-500 ease-premium group-hover:scale-105 md:h-16 md:w-16">
          {open ? (
            <X className="h-6 w-6 text-cream-50" />
          ) : (
            <img src="/assets/logo-mark-light.png" alt="Ada" className="h-5 w-auto md:h-6" />
          )}
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 top-16 z-[80] flex flex-col overflow-hidden rounded-t-3xl border border-charcoal-900/10 bg-white shadow-lift md:inset-x-auto md:bottom-48 md:right-8 md:top-auto md:h-[min(34rem,calc(100vh-14rem))] md:w-96 md:rounded-3xl"
          >
            <div className="flex items-center gap-3 bg-charcoal-900 px-5 py-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
                <img src="/assets/logo-mark-light.png" alt="Ada" className="h-4 w-auto" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg leading-tight text-cream-50">Ada Asistan</p>
                <p className="flex items-center gap-1.5 text-xs text-cream-50/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Çevrimiçi · Yapay zeka destekli
                </p>
              </div>
              <button
                data-testid="chat-widget-close"
                onClick={() => setOpen(false)}
                aria-label="Sohbeti kapat"
                className="rounded-full p-2 text-cream-50/70 transition-colors hover:bg-white/10 hover:text-cream-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={scrollRef} data-lenis-prevent data-testid="chat-messages-area" className="flex-1 space-y-4 overflow-y-auto overscroll-contain bg-cream-50 px-4 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "" : "flex items-end gap-2"}`}>
                    {m.role === "assistant" && (
                      <span className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-charcoal-900">
                        <img src="/assets/logo-mark-light.png" alt="" className="h-2.5 w-auto" />
                      </span>
                    )}
                    <div>
                      <div
                        data-testid={m.role === "user" ? "chat-user-message" : "chat-assistant-message"}
                        className={`whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                          m.role === "user"
                            ? "rounded-br-md bg-charcoal-900 text-cream-50"
                            : "rounded-bl-md border border-charcoal-900/5 bg-white text-charcoal-900 shadow-sm"
                        }`}
                      >
                        {m.text || (
                          <span className="flex gap-1 py-1">
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40 [animation-delay:0ms]" />
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40 [animation-delay:150ms]" />
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-charcoal-900/40 [animation-delay:300ms]" />
                          </span>
                        )}
                      </div>
                      {m.whatsapp && (
                        <a
                          data-testid="chat-whatsapp-cta"
                          href={wa(D.site, "Merhaba, yurdunuz hakkında detaylı bilgi almak istiyorum.")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-transform duration-300 ease-premium hover:scale-[1.03]"
                        >
                          <WhatsAppIcon className="h-4 w-4" />
                          WhatsApp'tan Detaylı Bilgi Alın
                        </a>
                      )}
                      {m.appointment && <AppointmentForm />}
                    </div>
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion-chip"
                      onClick={() => send(s)}
                      className="rounded-full border border-charcoal-900/10 bg-white px-3.5 py-2 text-xs font-medium text-charcoal-900/80 transition-colors duration-300 hover:border-charcoal-900/30 hover:bg-charcoal-900 hover:text-cream-50"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-charcoal-900/5 bg-white px-4 pb-2.5 pt-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
                className="flex items-center gap-2"
              >
                <input
                  data-testid="chat-message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  maxLength={1000}
                  className="flex-1 rounded-full border border-charcoal-900/10 bg-cream-50 px-4 py-2.5 text-sm text-charcoal-900 outline-none transition-colors placeholder:text-charcoal-900/40 focus:border-charcoal-900/30"
                />
                <button
                  data-testid="chat-send-button"
                  type="submit"
                  disabled={loading || !input.trim()}
                  aria-label="Gönder"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-charcoal-900 text-cream-50 transition-all duration-300 ease-premium hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
              <p data-testid="chat-powered-by" className="pt-2 text-center font-accent text-[10px] uppercase tracking-[0.2em] text-charcoal-900/35">
                Powered by CO Group
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
